import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider, useTheme } from "@/context/ThemeContext";
import { DEFAULT_THEME } from "@/lib/themes";
import ProtectedRoute from "@/components/ProtectedRoute";
import LoginPage from "@/pages/Login";
import AdminDashboard from "@/pages/AdminDashboard";
import OperatorPanel from "@/pages/OperatorPanel";
import TrackOrder from "@/pages/TrackOrder";
import MyOrders from "@/pages/MyOrders";
import DesignReview from "@/pages/DesignReview";
import FabricationDashboard from "@/pages/FabricationDashboard";
import DeliveryPanel from "@/pages/DeliveryPanel";
import { Toaster } from "@/components/ui/sonner";

const ADMIN_ROLES = ["admin", "secondary_admin", "accountant"];

function _roleHome(role) {
  if (ADMIN_ROLES.includes(role)) return "/admin";
  if (role === "fabrication") return "/fabrication";
  if (role === "delivery") return "/delivery";
  return "/operator";
}

function IndexRedirect() {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500 font-mono-num">
        Booting…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={_roleHome(user.role)} replace />;
}

function LoginRedirect() {
  const { user } = useAuth();
  if (user && user !== null) {
    return <Navigate to={_roleHome(user.role)} replace />;
  }
  return <LoginPage />;
}

function ThemeSync() {
  const { user } = useAuth();
  const { setThemeKey } = useTheme();
  React.useEffect(() => {
    if (user === false) setThemeKey(DEFAULT_THEME);
    else if (user) setThemeKey(user.theme || DEFAULT_THEME);
  }, [user, setThemeKey]);
  return null;
}

export default function App() {
  return (
    <ThemeProvider>
      <div className="App min-h-screen bg-slate-900 text-white">
        <AuthProvider>
        <ThemeSync />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<IndexRedirect />} />
            <Route path="/login" element={<LoginRedirect />} />
            <Route path="/track/:orderNumber" element={<TrackOrder />} />
            <Route path="/design-review/:orderNumber" element={<DesignReview />} />
            <Route path="/my-orders/:mobile" element={<MyOrders />} />
            <Route
              path="/fabrication"
              element={
                <ProtectedRoute roles={["fabrication"]}>
                  <FabricationDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute roles={ADMIN_ROLES}>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/operator"
              element={
                <ProtectedRoute roles={["operator"]}>
                  <OperatorPanel />
                </ProtectedRoute>
              }
            />
            <Route
              path="/delivery"
              element={
                <ProtectedRoute roles={["delivery", "admin", "secondary_admin"]}>
                  <DeliveryPanel />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster
            position="top-right"
            toastOptions={{
              classNames: {
                toast: "!rounded-sm !font-sans",
              },
              style: {
                borderRadius: "2px",
                fontFamily: "'IBM Plex Sans', sans-serif",
              },
            }}
          />
        </BrowserRouter>
      </AuthProvider>
      </div>
    </ThemeProvider>
  );
}
